import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { LockClosedIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { gateApi } from '../services/api';
import useStore from '../store/useStore';

function GateAuth() {
  const { setGateAuthenticated } = useStore();
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await gateApi.login(password);
      if (response.data.success) {
        setGateAuthenticated(true);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Authentication failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-galaxy-950 via-galaxy-900 to-galaxy-950 flex items-center justify-center p-4">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-galaxy-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="bg-galaxy-900/50 backdrop-blur-xl rounded-2xl border border-galaxy-700/50 p-8 shadow-2xl">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-galaxy-500 to-galaxy-700 mb-4">
              <LockClosedIcon className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Legal System</h1>
            <p className="text-galaxy-400">Enter password to access</p>
          </div>

          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-3 bg-red-900/30 border border-red-700/50 rounded-lg flex items-center gap-2"
            >
              <ExclamationCircleIcon className="w-5 h-5 text-red-400" />
              <span className="text-red-300 text-sm">{error}</span>
            </motion.div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="mb-6">
              <label htmlFor="password" className="block text-sm font-medium text-galaxy-300 mb-2">
                Password
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-galaxy-800/50 border border-galaxy-700/50 rounded-lg text-white placeholder-galaxy-500 focus:outline-none focus:border-galaxy-500 focus:ring-1 focus:ring-galaxy-500 transition-colors"
                placeholder="Enter demo password"
                autoFocus
              />
            </div>

            <button
              type="submit"
              disabled={isLoading || !password}
              className={`w-full py-3 px-4 rounded-lg font-medium transition-all ${
                isLoading || !password
                  ? 'bg-galaxy-700 text-galaxy-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-galaxy-500 to-galaxy-600 text-white hover:from-galaxy-400 hover:to-galaxy-500'
              }`}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Authenticating...
                </span>
              ) : (
                'Access Demo'
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="mt-6 pt-6 border-t border-galaxy-700/50 text-center">
            <p className="text-xs text-galaxy-500">
              Legal Document Management Demo
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default GateAuth;
